import type { Editor } from "@tiptap/core";
import { useEffect } from "react";
import { normalizeReport } from "./referencesPanelModel";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Hands the editor this document's reference index, so each reference in the
 * prose can show what the PDF will print. Fetched on open and again after each
 * save — a label added a moment ago must stop reading as dangling once it is
 * on the server. Without `?numbers=true`: the stored numbers from the last PDF
 * compile come free, and a compile per save would not.
 *
 * A failed fetch keeps whatever the editor last had rather than blanking it.
 */
export function useReferenceIndex(
  editor: Editor | null,
  documentId: string | undefined,
  fetchWithAuth: Fetch,
  refreshKey?: unknown,
) {
  useEffect(() => {
    if (!editor || !documentId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/documents/${documentId}/references`);
        if (!res.ok) return;
        const report = normalizeReport(await res.json());
        if (!cancelled && !editor.isDestroyed) editor.commands.setReferenceIndex(report.targets);
      } catch {
        /* keep the last index */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editor, documentId, fetchWithAuth, refreshKey]);
}
